/* @flow */
import { isEqual } from 'lodash';

import ActivityConversationHeader from 'chat/components/ActivityConversationHeader.jsx';
import { ChatMessage, ChatGapMessage } from 'entities/ChatMessage';
import { markChange } from 'lib/Timer';
import { mountModal } from 'lib/modal';
import { promiseRelationship } from 'chat/utils/ChatPromises';
import { PromptNames } from 'constants/PromptConstants';
import ChatActions from 'chat/actions/ChatActions';
import ChatConstants from 'chat/constants/ChatConstants';
import ChatMembers from 'collections/ChatMembers';
import ChatMemberships from 'collections/ChatMemberships';
import ChatMessageActions from 'chat/actions/ChatMessageActions';
import ChatMessageStore from 'chat/stores/ChatMessageStore';
import ChatStore from 'chat/stores/ChatStore';
import ConnectionState from 'stores/ConnectionState';
import ConversationHeader from 'chat/components/ConversationHeader.jsx';
import ConversationHeaderSidebarButton from 'chat/components/ConversationHeaderSidebarButton.jsx';
import ConversationSidebar from 'chat/components/ConversationSidebar.jsx';
import ConversationViewComposer from 'chat/components/ConversationViewComposer.jsx';
import CurrentUser from 'auth/CurrentUser';
import cx from 'classnames';
import getRoleDescriptionForChat from 'lib/getRoleDescriptionForChat.js';
import Group from 'entities/Group';
import linkStateWithStores from 'components/mixins/linkStateWithStores';
import MessageList from 'chat/components/MessageList.jsx';
import ActivityShareModal, { SHARE_MODAL } from 'payments/components/ActivityShareModal.jsx';
import PromptActions from 'actions/PromptActions';
import { showPromptDialog } from 'components/promptDialog.jsx';
import PromptStore from 'stores/PromptStore';
import React from 'react';
import StudentsParentsDialog from 'user/components/StudentsParentsDialog.jsx';
import ToastActions from 'actions/ToastActions';
import Chat from 'entities/Chat';
import UnreadStore from 'stores/UnreadStore';
import mountTymkPrompt from 'utils/mountTymkPrompt';

import type { RouterState, LocationDescriptor } from 'lib/ReactRouterTypes';
import type { MessageType } from 'chat/lib/MessageInterfacePropType';

import './styles/ConversationView.scss';

type State = {
  chat: Chat,
  chatMemberships: ChatMemberships,
  messages: MessageType[],
  sidebar: boolean,
  unreadCount: number,
};

type Props = {
  params: Object,
};

function checkLoadChats(chatId: string): void {
  const messages = ChatMessageStore.getChatMessages(chatId);
  if (messages.isEmpty() && chatId) {
    ChatMessageActions.loadChatMessages(chatId);
  }
}

export const ConversationView = React.createClass({
  propTypes: {
    params: React.PropTypes.object.isRequired,
  },

  contextTypes: {
    router: React.PropTypes.object.isRequired,
    group: React.PropTypes.instanceOf(Group).isRequired,
  },


  mixins: [
    linkStateWithStores([ChatMessageStore, ChatStore, UnreadStore]),
  ],

  statics: {
    onLeaveNewChat() {
      // Called when leaving the /new route, destroys the local chat
      ChatActions.destroyLocal();
    },
    onEnter(nextState: RouterState, replaceWith: (location: LocationDescriptor) => void, callback: Function) {
      const { params, location } = nextState;
      const { query } = location;
      const { memberId, chatId, groupCode } = params;

      // FIXME I think all we need use to put the 'announcements' route before the 'chatId' route
      // We must perform this redirect here because we can't distinguish between 'announcements'
      // and any other chatId -- even if there is an explicit route for /announcements(/), this
      // onEnter will fire with chatId === 'announcements'
      if (chatId === 'announcements') {
        replaceWith({ pathname: `/classes/${groupCode}/messages/announcements/everyone`, query });
        callback();
        return;
      }

      if (memberId) {
        ConversationView.createLocalChat(memberId).then(callback);
      } else if (chatId) {
        const chat = ChatStore.getChat(chatId);
        if (chat) {
          callback();
        } else {
          ChatActions.loadChat(chatId).then(callback);
        }
      } else {
        // this includes group chats, for which the local is already created
        callback();
      }
    },
    createLocalChat(memberIdStr) {
      const memberId = parseInt(memberIdStr, 10);
      const currentMember = ChatMembers.buildChatMember(CurrentUser.getUser());
      return promiseRelationship(memberId).then(relationship => {
        const member = ChatMembers.buildChatMember(relationship.getRelatedUser());
        return ChatActions.loadLocal([member, currentMember], member.getName());
      });
    },
  },

  getInitialState(): State {
    const { chatId } = this.props.params;
    const chat = chatId ? ChatStore.getChat(chatId) : null;
    const chatOrLocalChat = chat || ChatStore.getLocal();
    const messages = chatId ? ChatMessageStore.getChatMessages(chatId).models.slice(0) : [];

    return Object.assign(this.getChatState(chatOrLocalChat, messages), {
      sidebar: false,
    });
  },

  componentWillMount() {
    const { chatId } = this.props.params;
    if (chatId) {
      checkLoadChats(chatId);
    }
  },

  componentDidMount() {
    markChange('chat_open', 'chat_shown');

    if (this.context.router.location.query.show === SHARE_MODAL) {
      mountModal(<ActivityShareModal activity={this.state.chat.getActivity()} />, this);
    }

    if (this.shouldShowStudentParentCreateGroupModal()) {
      mountModal(<StudentsParentsDialog onOkay={this.onStudentsParentsDialogOkay} />, this);
      PromptActions.recordDismissal(PromptNames.SP_CREATE_GROUP_MODAL);
    } else {
      this.focusComposer();
    }
    ConnectionState.on('reconnect', this.handleReconnect);
  },

  componentWillReceiveProps(nextProps: Props) {
    this.setState(this.getStateFromStores(nextProps)); // why?

    const { chatId } = nextProps.params;
    if (!chatId) {
      return;
    }

    if (ChatMessageStore.shouldReloadMessages(chatId)) {
      ChatMessageActions.loadChatMessages(chatId);
    }
    checkLoadChats(chatId);
  },

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    // shallow state
    const shallowKeys = [
      'chat',
      'sidebar',
      'unreadCount',
    ];

    for (const key of shallowKeys) {
      if (this.state[key] !== nextState[key]) {
        return true;
      }
    }

    if (!isEqual(this.props.params, nextProps.params)) {
      return true;
    }

    if (this.state.messages.length !== nextState.messages.length) {
      return true;
    }

    const oldChatMemberships = this.state.chatMemberships;
    const newChatMemberships = nextState.chatMemberships;
    const membershipsHaveChanged =
      oldChatMemberships !== newChatMemberships ||
      oldChatMemberships.length !== newChatMemberships.length ||
      (!!oldChatMemberships && !!newChatMemberships &&
        oldChatMemberships.some((o, i) => !isEqual(o.attributes, newChatMemberships.at(i).attributes)));
    if (membershipsHaveChanged) {
      return true;
    }

    return false;
  },

  componentDidUpdate(prevProps: Props, prevState: Object) {
    if (prevProps.params.chatId === this.props.params.chatId) {
      const currentLastMessage = this.state.messages[this.state.messages.length - 1];
      const prevLastMessage = prevState.messages[prevState.messages.length - 1];
      if (currentLastMessage !== prevLastMessage) {
        markChange('chat_open', 'last_message_shown');
      }
    } else {
      // focusComposer() does not work without setTimeout(,1) after DOM update
      setTimeout(() => {
        this.focusComposer();
      }, 1);

      // chatId changed
      markChange('chat_open', 'chat_shown');
    }
  },

  componentWillUnmount() {
    ConnectionState.removeListener('reconnect', this.handleReconnect);

    // Clear local chat if we have not already before unmounting
    if (ChatStore.hasLocal()) {
      ChatActions.destroyLocal();
    }
  },

  toggleSidebar() {
    this.setState(({ sidebar }) => ({ sidebar: !sidebar }));
  },

  onStudentsParentsDialogOkay() {
    this.context.router.push('/classes'); // almost certainly incorrect
  },

  getChatState(chat: Chat, messages: MessageType[]): Object {
    return {
      chat,
      chatMemberships: chat.getMemberships(),
      messages,
      unreadCount: chat.isNew() ? 0 : UnreadStore.getUnreadCount(chat.id),
    };
  },

  // $FlowIssue default value
  getStateFromStores(props = this.props): Object {
    const { chatId } = props.params;

    const chat = chatId ? ChatStore.getChat(chatId) : null;
    const chatOrLocalChat = chat || ChatStore.getLocal();
    const nextMessages = chatId ? ChatMessageStore.getChatMessages(chatId).models.slice(0) : [];

    // Do not change state for null chat
    const [stateChat, stateMessages] = chatOrLocalChat ?
      [chatOrLocalChat, nextMessages] :
      [this.state.chat, this.state.messages];

    return this.getChatState(stateChat, stateMessages);
  },

  // Invoked upon message send in a new chat thread, this creates the chat thread
  // on the server, and destroys the local chat
  initialize(text: string, files: Array<*>) {
    const sender = CurrentUser.getUser();
    const { group } = this.context;
    const originGroupId = group.id > 0 ? group.id : null;
    const message = ChatMessage.create(text, files, this.state.chat, sender, 0, 'text', null, originGroupId);
    return ChatActions.createChat(this.state.chat, message).then(chat => {
      this.context.router.replace(`/classes/${group.getCode()}/messages/${chat.id}`);
      ChatActions.destroyLocal();
      mountTymkPrompt(this, 'send_chat');
    }).catch(error => {
      if (error && ['no_solo_chats', 'max_memberships'].indexOf(error.code) > -1) {
        ToastActions.showErrorToast(error.message);
      } else {
        throw error;
      }
    });
  },

  // If no chatId is passed in the url, this must be a new chat thread
  isNewConversation() {
    const { params } = this.props;
    return (!params || !params.chatId);
  },

  loadMoreMessages(message: ChatGapMessage) {
    ChatMessageActions.fillGap(message);
  },

  focusComposer() {
    // Check for truthy refs.composer is defensive, focusComposer is used in a setTimeout()
    if (ChatStore.getFocusView() === ChatConstants.FocusableViews.COMPOSER
      && this.refs.composer) {
      this.refs.composer.getWrappedInstance().focus();
    }
  },

  handleReconnect() {
    const { chatId } = this.props.params;
    ChatMessageActions.loadChatMessages(chatId);
  },

  openSideBar() {
    if (!this.state.sidebar) {
      this.toggleSidebar();
    }
  },

  sendMessage(text: string, files: Array<*>) {
    const sender = CurrentUser.getUser();
    const lastMessage = ChatMessageStore.getChatMessages(this.state.chat.id).last();
    const seq = lastMessage ? lastMessage.getSeq() + 1 : 1;
    const { group } = this.context;
    const originGroupId = group.id > 0 ? group.id : null;
    const message = ChatMessage.create(text, files, this.state.chat, sender, seq, 'text', null, originGroupId);

    ChatMessageActions.sendMessage(message).then(() => {
      showPromptDialog(PromptNames.DESKTOP_NOTIFICATION_MODAL, 'sent_chat', this);
    });

    mountTymkPrompt(this, 'send_chat');
    this.refs.messageList.scrollToBottom();
  },

  shouldShowStudentParentCreateGroupModal() {
    return CurrentUser.getUser().getPromptsInventory()
      .find(i => i.get('action') === PromptNames.SP_CREATE_GROUP_MODAL) &&
      !PromptStore.hasDismissed(PromptNames.SP_CREATE_GROUP_MODAL);
  },

  chatHasSidebar() {
    return !this.state.chat.isNew();
  },

  updateUnread() {
    const { chat } = this.state;
    if (this.state.unreadCount > 0) {
      const lastMessage = this.state.messages[this.state.messages.length - 1];
      if (lastMessage) {
        chat.markAsReadForAssociatedGroups(UnreadStore);
        // $FlowIssue
        ChatActions.markReadUpto(chat.id, lastMessage.getSeq());
      }
    }
  },

  renderSideBar() {
    if (!this.state.sidebar) {
      return null;
    }

    const { chat } = this.state;

    return (
      <ConversationSidebar
        className="ConversationView-body-sidebar"
        chat={chat}
      />
    );
  },

  renderHeaderButtons() {
    const { chat } = this.state;
    return this.chatHasSidebar() && (
      <ConversationHeaderSidebarButton
        chat={chat}
        className="ConversationView-header-sidebarButton"
        sidebar={this.state.sidebar}
        onSideBarToggle={this.toggleSidebar}
      />
    );
  },

  render() {
    const { chat } = this.state;
    if (!chat) {
      return null;
    }

    let header;
    const isActivity = chat.isActivityType();

    if (isActivity) {
      header = (
        <ActivityConversationHeader
          ref="header"
          chat={chat}
          sidebar={this.state.sidebar}
          activity={chat.getActivity()}
          className="ConversationView-header-activity-sender"
          onSideBarToggle={this.toggleSidebar}
        />
      );
    } else {
      header = (
        <div ref="header">
          <ConversationHeader
            chat={chat}
            sidebar={this.state.sidebar}
            subtitle={getRoleDescriptionForChat(chat)}
            onTitleClicked={this.chatHasSidebar() ? this.toggleSidebar : null}>
            {this.renderHeaderButtons()}
          </ConversationHeader>
        </div>
      );
    }

    return (
      <div className={cx('ConversationView', this.props.className)}>
        {header}
        <div className="ConversationView-body">
          <div className="ConversationView-body-wrap">
            <MessageList
              ref="messageList"
              className="ConversationView-body-messageList"
              chat={chat}
              unreadCount={this.state.unreadCount}
              messages={this.state.messages}
              loadMoreMessages={this.loadMoreMessages}
              onScrollToBottom={this.updateUnread}
            />
            <ConversationViewComposer
              ref="composer"
              className="ConversationView-body-composer"
              chat={chat}
              onMessageSend={this.isNewConversation() ? this.initialize : this.sendMessage}
              openSideBar={this.openSideBar}
            />
          </div>
          {this.renderSideBar()}
        </div>
      </div>
    );
  },
});

export default ConversationView;



// WEBPACK FOOTER //
// ./src/chat/components/ConversationView.jsx